/**
 * Admin Dashboard Component
 * Placeholder welcome page for admin panel
 * 
 * Security:
 * - Checks for valid token in sessionStorage
 * - Redirects to login if token is missing/invalid
 * - Logout clears sessionStorage
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { testWebsiteFirestoreConnection } from '../../../config/firebaseConfig';

const MASTER_TOKEN = (import.meta as any).env?.VITE_ADMIN_MASTER_TOKEN || '';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [testLogs, setTestLogs] = useState<string[]>([]);
  const [isTesting, setIsTesting] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Verify token on mount
    const storedToken = sessionStorage.getItem('adminToken');
    
    if (storedToken === MASTER_TOKEN && MASTER_TOKEN) {
      setIsAuthorized(true);
    } else {
      // Invalid or missing token - redirect to login
      sessionStorage.removeItem('adminToken');
      navigate('/x9s4k-portal-72/admin/login', { replace: true });
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    navigate('/x9s4k-portal-72/admin/login', { replace: true });
  };

  // Auto-scroll logs to bottom
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [testLogs]);

  const handleTestFirestore = async () => {
    setIsTesting(true);
    setTestLogs([]);
    
    // Capture console logs
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    
    const capturedLogs: string[] = [];
    
    const addLog = (message: string, type: 'log' | 'error' | 'warn' = 'log') => {
      const timestamp = new Date().toLocaleTimeString();
      const prefix = type === 'error' ? '❌' : type === 'warn' ? '⚠️' : '✅';
      // Remove [TEST] prefix if present, clean up message
      const cleanMessage = message.replace(/\[TEST\]\s*/g, '').trim();
      capturedLogs.push(`[${timestamp}] ${prefix} ${cleanMessage}`);
      setTestLogs([...capturedLogs]);
    };
    
    // Helper to format console arguments
    const formatMessage = (...args: any[]): string => {
      return args.map(arg => {
        if (arg === null) return 'null';
        if (arg === undefined) return 'undefined';
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');
    };
    
    // Override console methods
    console.log = (...args: any[]) => {
      originalLog(...args);
      const message = formatMessage(...args);
      if (message.includes('[TEST]')) {
        addLog(message, 'log');
      }
    };
    
    console.error = (...args: any[]) => {
      originalError(...args);
      const message = formatMessage(...args);
      if (message.includes('[TEST]')) {
        addLog(message, 'error');
      }
    };
    
    console.warn = (...args: any[]) => {
      originalWarn(...args);
      const message = formatMessage(...args);
      if (message.includes('[TEST]')) {
        addLog(message, 'warn');
      }
    };
    
    try {
      addLog('Starting Firestore connection test...', 'log');
      await testWebsiteFirestoreConnection();
      addLog('Test completed. Check logs above for results.', 'log');
    } catch (error: any) {
      const errorMessage = error?.message || String(error) || 'Unknown error';
      addLog(`Test failed with error: ${errorMessage}`, 'error');
      if (error?.code) {
        addLog(`Error code: ${error.code}`, 'error');
      }
    } finally {
      // Restore original console methods
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
      setIsTesting(false);
    }
  };

  // Show loading while checking authorization
  if (isAuthorized === null || isAuthorized === false) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '4rem',
          fontWeight: 800,
          color: 'rgba(255, 255, 255, 0.3)',
          pointerEvents: 'none',
          userSelect: 'none'
        }}>
          Hello
        </div>
        <div style={{ color: '#ffffff', fontSize: '1.25rem', position: 'relative', zIndex: 1 }}>Verifying access...</div>
      </div>
    );
  }

  // Debug indicator
  console.log('[ADMIN] AdminDashboard component rendered', { 
    isAuthorized,
    url: window.location.href 
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      position: 'relative'
    }}>
      {/* Debug indicator - "Hello" in the center background */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '6rem',
        fontWeight: 800,
        color: 'rgba(102, 126, 234, 0.1)',
        pointerEvents: 'none',
        zIndex: 0,
        userSelect: 'none'
      }}>
        Hello
      </div>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#ffffff',
        padding: '20px 40px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 800,
            letterSpacing: '-0.02em'
          }}>
            Gig Genie Admin
          </h1>
          <button
            onClick={handleLogout}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              padding: '10px 24px',
              borderRadius: '50px',
              cursor: 'pointer',
              fontWeight: 600,
              transition: 'all 0.3s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.7)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        padding: '40px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <div style={{
          background: '#ffffff',
          padding: '60px 40px',
          borderRadius: '20px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '20px'
          }}>
            👋
          </div>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 800,
            marginBottom: '16px',
            color: '#1a1a1a'
          }}>
            Welcome Admin
          </h2>
          <p style={{
            fontSize: '1.25rem',
            color: '#495057',
            lineHeight: 1.7,
            marginBottom: '40px',
            maxWidth: '600px',
            margin: '0 auto 40px'
          }}>
            Manage users, memberships, and view analytics from the admin panel.
          </p>

          {/* Navigation Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginTop: '40px',
            maxWidth: '1000px',
            margin: '40px auto 0'
          }}>
            {/* User Management Card */}
            <Link
              to="/x9s4k-portal-72/admin/users"
              style={{
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              <div style={{
                background: '#ffffff',
                border: '2px solid #e9ecef',
                borderRadius: '15px',
                padding: '30px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderColor = '#667eea';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.borderColor = '#e9ecef';
              }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '15px' }}>👥</div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  marginBottom: '10px',
                  color: '#1a1a1a'
                }}>
                  User Management
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#6c757d',
                  lineHeight: 1.6
                }}>
                  View, search, and manage user accounts
                </p>
              </div>
            </Link>

            {/* Membership & Promotions Card */}
            <Link
              to="/x9s4k-portal-72/admin/memberships"
              style={{
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              <div style={{
                background: '#ffffff',
                border: '2px solid #e9ecef',
                borderRadius: '15px',
                padding: '30px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderColor = '#667eea';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.borderColor = '#e9ecef';
              }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '15px' }}>💎</div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  marginBottom: '10px',
                  color: '#1a1a1a'
                }}>
                  Memberships & Promos
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#6c757d',
                  lineHeight: 1.6
                }}>
                  Manage memberships and promo codes
                </p>
              </div>
            </Link>

            {/* Analytics Card */}
            <Link
              to="/x9s4k-portal-72/admin/analytics"
              style={{
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              <div style={{
                background: '#ffffff',
                border: '2px solid #e9ecef',
                borderRadius: '15px',
                padding: '30px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderColor = '#667eea';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.borderColor = '#e9ecef';
              }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📊</div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  marginBottom: '10px',
                  color: '#1a1a1a'
                }}>
                  Analytics Dashboard
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#6c757d',
                  lineHeight: 1.6
                }}>
                  View app metrics and user analytics
                </p>
              </div>
            </Link>
          </div>

          {/* Dev Tools Section */}
          <div style={{
            marginTop: '60px',
            paddingTop: '40px',
            borderTop: '2px solid #e9ecef'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              cursor: 'pointer'
            }}
            onClick={() => setShowDevTools(!showDevTools)}
            >
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#1a1a1a',
                margin: 0
              }}>
                🔧 Dev Tools
              </h3>
              <span style={{
                fontSize: '1.25rem',
                color: '#6c757d',
                transition: 'transform 0.3s',
                transform: showDevTools ? 'rotate(180deg)' : 'rotate(0deg)'
              }}>
                ▼
              </span>
            </div>

            {showDevTools && (
              <div style={{
                background: '#ffffff',
                border: '2px solid #e9ecef',
                borderRadius: '15px',
                padding: '30px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px'
                }}>
                  <button
                    onClick={handleTestFirestore}
                    disabled={isTesting}
                    style={{
                      background: isTesting 
                        ? '#6c757d' 
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#ffffff',
                      border: 'none',
                      padding: '15px 30px',
                      borderRadius: '50px',
                      fontSize: '1rem',
                      fontWeight: 600,
                      cursor: isTesting ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s',
                      opacity: isTesting ? 0.7 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!isTesting) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                      }
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {isTesting ? 'Testing...' : '🧪 Test Firestore Connection'}
                  </button>

                  {testLogs.length > 0 && (
                    <div style={{
                      background: '#1a1a1a',
                      borderRadius: '10px',
                      padding: '20px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      lineHeight: 1.6
                    }}>
                      {testLogs.map((log, index) => {
                        const isError = log.includes('❌') || log.toLowerCase().includes('failed') || log.toLowerCase().includes('error');
                        const isSuccess = log.includes('✅') || log.toLowerCase().includes('success') || log.toLowerCase().includes('passed');
                        const isWarning = log.includes('⚠️') || log.toLowerCase().includes('warning') || log.toLowerCase().includes('permission');
                        
                        return (
                          <div
                            key={index}
                            style={{
                              color: isError 
                                ? '#ff6b6b' 
                                : isSuccess 
                                ? '#51cf66' 
                                : isWarning
                                ? '#ffd43b'
                                : '#e9ecef',
                              marginBottom: '8px',
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {log}
                          </div>
                        );
                      })}
                      <div ref={logsEndRef} />
                    </div>
                  )}

                  {testLogs.length === 0 && !isTesting && (
                    <div style={{
                      color: '#6c757d',
                      fontSize: '0.875rem',
                      textAlign: 'center',
                      padding: '20px',
                      background: '#f8f9fa',
                      borderRadius: '10px'
                    }}>
                      Click "Test Firestore Connection" to verify website connectivity to Firestore.
                      <br />
                      The test will write, read, and delete a test document from analytics/events.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

